import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import fusionMsiStar from "assets/images/logos/fusionmsi-logo.svg";

/**
 * Covers the screen once it has gone idle. Pair with useIdleBlur.
 *
 * ⚠️ IT COVERS THE VIEWPORT, NOT A PANEL, and that is the whole point of the piece. It began
 * as an absolutely positioned cover over the encounter column and it looked cheap for a
 * reason that was not cosmetic: the sidebar, the header and the page gutters stayed sharp
 * around a blurred rectangle, so anyone in the room could see it was the same app with one
 * part hidden, and anything the shell happened to be showing was never covered at all. A
 * privacy cover that covers part of a screen is not a privacy cover.
 *
 * The old version was also centred inside a column as tall as the whole encounter, which put
 * its message somewhere down the page rather than in front of the reader. Fixed positioning
 * fixes both: one element, the size of the window, with its message in the middle of it.
 *
 * ⚠️ IT REVEALS ON ONE TAP AND ASKS FOR NOTHING. No password, no PIN. Re-authentication after
 * inactivity is a separate concern that useIdleTimer already owns at the session level; making
 * a clinician prove who they are to see a screen they are standing in front of would cost more
 * than it protects.
 *
 * The whole surface is the control rather than a button inside it, so a glance-and-tap works
 * anywhere and there is no nested interactive element to trip over.
 */
const IdleBlurOverlay = ({
  open,
  onReveal,
  message = "Hidden for privacy while the screen was idle. Please click anywhere to show it.",
}) => {
  if (!open) return null;

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onReveal?.();
  };

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={message}
      onClick={onReveal}
      onKeyDown={handleKeyDown}
      sx={{
        position: "fixed",
        inset: 0,
        // Above the dashboard shell and above any dialog left open on the screen. A cover that
        // a modal can sit on top of is not covering anything.
        zIndex: (theme) => theme.zIndex.modal + 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        // ⚠️ THE BLUR DOES THE WORK, NOT THE TINT, and the balance between them matters. A
        // near-opaque wash hides the page so completely that it reads as lost rather than
        // covered, and the clinician's first thought is that their encounter is gone. Heavy
        // blur over a light tint leaves the shape and colour of the page plainly visible,
        // which says "still here, just not readable", while nothing on it can be read from
        // across a room.
        backdropFilter: "blur(18px) saturate(1.1)",
        WebkitBackdropFilter: "blur(18px) saturate(1.1)",
        // Releases without backdrop-filter get this alone. It is the weaker of the two, which
        // is the deliberate trade: every browser this ships to supports backdrop-filter.
        backgroundColor: (theme) => alpha(theme.palette.background.default, 0.45),
      }}
    >
      {/* No card behind this. One was tried on the assumption that text over a lightly
          tinted blur would be hard to read, and it is not: the blur destroys the detail the
          text has to compete with, so the words sit on what is effectively a flat wash. The
          card only added a second rectangle to look at. */}
      <Stack spacing={1.5} alignItems="center" sx={{ px: 4, maxWidth: 360, textAlign: "center" }}>
        <Box
          component="img"
          src={fusionMsiStar}
          alt=""
          sx={{ height: 40, width: "auto", display: "block" }}
        />
        <Typography variant="body1" color="text.primary">
          {message}
        </Typography>
      </Stack>
    </Box>
  );
};

export default IdleBlurOverlay;
